// Central catalog of every live JupyterHub site+version CAMCEADHUB can
// route requests to. Added 2026-09-11 for the ARCUS/Imperial/STFC
// multi-site build - see CAMCEADHUB_SYSTEM_README.md.
//
// UX (2026-11-05: the Jupyter 3/4 version choice is gone, one hub per site): the
// *site* (ARCUS/Imperial/STFC) is chosen on the spawn page itself
// (JupyterLab.vue), alongside server type/image/GPU, since that's a
// per-spawn decision - picking STFC there defaults "Request a GPU" on,
// since STFC is the GPU site.
//
// Both pieces combine into one hub id, which is what actually gets sent
// as the `hub` (servers/token/progress/etc) and `hub_version` (images) API
// params - must match the camceadhub backend's camceadhub/jhub/utils.py
// `_HUB_PREFIXES` keys exactly, or the backend silently falls back to
// ARCUS v3 (get_hub_prefix()'s default).
export const HUB_SITES = [
  { id: "arcus", label: "ARCUS (Cambridge)", defaultGpu: false },
  { id: "imperial", label: "Imperial", defaultGpu: false },
  { id: "stfc", label: "STFC (GPU)", defaultGpu: true },
];

// JupyterHub 3 was retired on every site on 2026-11-05: there is exactly ONE hub per site now, so there is no version
// choice any more (the Profile dropdown is gone). The ids on the wire keep their old v4 spelling ("4" = ARCUS,
// "imperial-v4", "stfc-v4") so the backend's image catalog, tokens and prefixes are unchanged; the backend maps the
// old ids ("", "3", "imperial-v3", "stfc-v3") to the same ids (utils.normalize_hub), and normalizeHubId() below does
// the same for anything an old browser still holds.
export const DEFAULT_SITE_ID = "arcus";

// `urlPrefix` is the path segment under camceadhub.ast.cam.ac.uk that JupyterLabView.vue builds the live notebook
// iframe URL from - "" for ARCUS (its hub lives at the domain root, base_url "/"), "<prefix>/" for the other two,
// matching that hub's own `c.JupyterHub.base_url` and apm62 nginx's location block of the same name.
const HUB_URL_PREFIXES = {
  "4": "",
  "imperial-v4": "jhub-imperial-v4/",
  "stfc-v4": "jhub-stfc-v4/",
};

// Any hub id, old or new, as the current hub id of the same site.
export function normalizeHubId(id) {
  if (!id || id === "3" || id === "4") return "4";
  if (id === "imperial-v3" || id === "imperial-v4") return "imperial-v4";
  if (id === "stfc-v3" || id === "stfc-v4") return "stfc-v4";
  return "4";
}

// The hub id of a site (the version argument is ignored; kept so old call sites keep working).
export function hubId(siteId) {
  if (!siteId || siteId === "arcus") return "4";
  return `${siteId}-v4`;
}

// Inverse of hubId(): the site of a stored/combined hub id of any scheme.
export function siteAndVersionFromHubId(id) {
  const n = normalizeHubId(id);
  if (n === "imperial-v4") return { site: "imperial", version: "4" };
  if (n === "stfc-v4") return { site: "stfc", version: "4" };
  return { site: DEFAULT_SITE_ID, version: "4" };
}

export function hubUrlPrefix(id) {
  return HUB_URL_PREFIXES[normalizeHubId(id)] ?? "";
}

export function siteLabel(siteId) {
  const site = HUB_SITES.find((s) => s.id === siteId);
  return site ? site.label : HUB_SITES[0].label;
}

export function siteDefaultGpu(siteId) {
  const site = HUB_SITES.find((s) => s.id === siteId);
  return site ? site.defaultGpu : false;
}
